import clsx from 'clsx';
import LoadingSpinner from 'components/LoadingSpinner';
import { TruncatedText } from 'components/TruncatedText';
import { usePluginDashboardList } from 'queries/hooks/usePluginDashboardList';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { DashboardFolder, DashboardItem } from 'services/PluginService';
import { INDENT_PER_LEVEL } from 'ui/nav/components/dashboards/DashboardTreeUtils';
import { NavLink } from 'ui/nav/components/NavLink';
import { FolderChevron } from '../dashboards/DashboardTreeItem';

interface OobDashboardTreeProps {
    pluginId: string;
    configId: string;
}

export const OobDashboardTree: React.FC<OobDashboardTreeProps> = ({ pluginId, configId }) => {
    const [isOpen, setIsOpen] = useState<Record<string, boolean>>({});

    const { data: dashboards = [], isLoading, isFetched } = usePluginDashboardList(pluginId, configId);

    const pathMatch = useMatch('datasource/:id/dashboard/*');

    // react-router type for params doesn't include * for some reason
    const dashboardPathName = (pathMatch?.params as unknown as { '*': string })?.['*'] ?? '';

    useEffect(() => {
        // open folders leading to the dashboard
        const path = getPathToItem({
            current: dashboards,
            targetPath: dashboardPathName
        });
        if (path && path.length) {
            setIsOpen(
                path.reduce<Record<string, boolean>>((acc, cur) => {
                    acc[cur] = true;
                    return acc;
                }, {})
            );
        }
    }, [dashboardPathName, dashboards]);

    if (isLoading) {
        return (
            <div className='pl-10 mb-2'>
                <LoadingSpinner size={14} />
            </div>
        );
    }

    if (dashboards.length === 0) {
        if (isFetched) {
            return <p className='pl-10 mb-2'>No dashboards</p>;
        }
        return null;
    }

    return (
        <div className='relative overflow-hidden pl-[25px] flex flex-col'>
            <div
                data-type='border'
                className='absolute top-0 bottom-0 w-px border-l border-l-dividerTertiary'
                style={{
                    height: 'calc(100% - 8px)',
                    left: 25
                }}
            />
            {dashboards.map((item, index) => (
                <TreeItem
                    item={item}
                    level={0}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    currentDashboardPath={dashboardPathName}
                    getPath={(i) => `/datasource/${configId}/dashboard/${i.path}`}
                    isLastOfGroup={index === dashboards.length - 1}
                    key={item.path}
                    index={index}
                    treeId={'plugin-dashboard-tree'}
                />
            ))}
        </div>
    );
};

interface TreeItemProps {
    index: number;
    item: DashboardItem | DashboardFolder;
    isOpen: Record<string, boolean>;
    currentDashboardPath?: string;
    setIsOpen: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    getPath: (item: DashboardItem) => string;
    level: number;
    treeId: string;
    isLastOfGroup: boolean;
}

export const TreeItem = ({
    item,
    level,
    index,
    treeId,
    currentDashboardPath,
    getPath,
    isOpen,
    setIsOpen
}: TreeItemProps) => {
    const buttonRef = useRef<HTMLAnchorElement>(null);

    useEffect(() => {
        if (currentDashboardPath === item.path) {
            buttonRef.current?.scrollIntoView({ block: 'nearest' });
        }
    }, [currentDashboardPath, item.path]);

    return (
        <>
            <div
                data-type={item.isFolder ? 'folder' : 'dash'}
                className='relative flex items-center overflow-visible grow'
            >
                {!item.isFolder ? (
                    <DashboardComponent
                        id={`tree-item-${item.path}`}
                        item={item}
                        level={level}
                        ref={buttonRef}
                        getPath={getPath}
                        data-index={index}
                        data-level={level}
                    />
                ) : (
                    <FolderComponent
                        id={`tree-item-${item.path}`}
                        onClick={() => setIsOpen({ ...isOpen, [item.path]: !isOpen[item.path] })}
                        item={item}
                        level={level}
                        isOpen={isOpen[item.path]}
                        data-index={index}
                        data-level={level}
                    />
                )}
            </div>
            {item.isFolder && isOpen[item.path] ? (
                <div className='relative flex flex-col grow' data-type='content' id={`tree-item-${item.path}--subtree`}>
                    <div
                        data-type='border'
                        className='absolute top-0 bottom-0 w-px border-l border-l-dividerTertiary'
                        style={{
                            height: 'calc(100% - 6px)',
                            left: INDENT_PER_LEVEL * (level + 1) + 1
                        }}
                    />
                    {item.dashboards?.map((child, i) => (
                        <TreeItem
                            isLastOfGroup={i === item.dashboards.length - 1}
                            isOpen={isOpen}
                            currentDashboardPath={currentDashboardPath}
                            setIsOpen={setIsOpen}
                            getPath={getPath}
                            item={child}
                            key={child.path}
                            level={level + 1}
                            index={i}
                            treeId={treeId}
                        />
                    ))}
                </div>
            ) : null}
        </>
    );
};

type FolderComponentProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
    item: DashboardFolder;
    level: number;
    isOpen?: boolean;
};
const FolderComponent = forwardRef<HTMLSpanElement, FolderComponentProps>(
    ({ item, level, isOpen, className, ...props }, ref) => {
        return (
            <span
                draggable={false}
                {...props}
                className={clsx('flex grow w-full px-2.5 items-center group pb-[8px] relative', className)}
                ref={ref}
            >
                <button
                    style={{ paddingLeft: level * INDENT_PER_LEVEL + 9 }}
                    type='button'
                    className='flex basis-0 w-[calc(100%-17.5px)] grow py-px focus-visible:text-textPrimary text-textSecondary focus-visible:outline outline-2 outline-current  leading-input items-center group/btn'
                    aria-expanded={Boolean(isOpen)}
                    aria-controls={`tree-item-${item.path}--subtree`}
                    aria-label='ExpandCollapse Folder'
                >
                    <FolderChevron />
                    <TruncatedText
                        placement='right'
                        className='text-left truncate group-hover:text-textPrimary'
                        title={item.name}
                    >
                        {item.name}
                    </TruncatedText>
                </button>
            </span>
        );
    }
);

type DashboardComponentProps = React.ButtonHTMLAttributes<HTMLAnchorElement> & {
    item: DashboardItem;
    level: number;
    getPath: (item: DashboardItem) => string;
};
const DashboardComponent = forwardRef<HTMLAnchorElement, DashboardComponentProps>(
    ({ item, level, className, getPath, ...props }, ref) => (
        <span ref={ref} className={clsx('pb-[5.5px] w-full px-2.5 relative', className)} {...props}>
            <NavLink
                data-testid='dashboardsLink'
                draggable={false}
                style={{ paddingLeft: level * INDENT_PER_LEVEL + 9 }}
                to={getPath(item)}
                className={'!py-0.5 pr-1 flex grow focus-visible:outline outline-current outline-2'}
            >
                <TruncatedText placement='right' className='truncate shrink first:ml-[1px]' title={item.name || ''}>
                    {item.name}
                </TruncatedText>
            </NavLink>
        </span>
    )
);

const getPathToItem = ({
    current,
    targetPath,
    parentIds = []
}: {
    current: (DashboardFolder | DashboardItem)[];
    targetPath: string;
    parentIds?: string[];
}): string[] | undefined => {
    for (const item of current) {
        if (item.path === targetPath) {
            return parentIds;
        }
        if (item.isFolder && item.dashboards) {
            const nested = getPathToItem({
                current: item.dashboards,
                targetPath: targetPath,
                parentIds: [...parentIds, item.path]
            });
            if (nested) {
                return nested;
            }
        }
    }
};
